import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Divider, List, ListItem, ListItemText, Chip } from '@mui/material';
import { useCurrency } from '../../components/CurrencyContext';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient, useApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";

//Cards
import ValuationHeader from '../Valuations/ValuationHeader';
import ValuationCards from '../Valuations/ValuationCards';
import { ValuationCard, Valuation, ValuationHeaderData } from '../../models/Valuation';
import './ValuationCards.css';

//Grid
import { ValuationGrid } from "./ValuationsGrid";

//Add/Edit
import AddValuationDialog from "./AddValuationDialog";

export  type Props = {
    artworkId: number;
    onAdd: () => void;
    reloadToken: number
};

const ValuationsPanel: React.FC<Props> = (
    { artworkId, onAdd, reloadToken }) => {

    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const { currency } = useCurrency();
    const [conversionRates, setConversionRates] = useState<{ [key: string]: number } | null>(null);
    const [vals, setVals] = React.useState<Valuation[] | null>(null);
    const [valuationHeader, setValuationHeader] = useState<ValuationHeaderData | null>(null);
    const [latestValuation, setLatestValuation] = React.useState<ValuationCard>();
    const [loadingValuation, setLoadingValuation] = React.useState(true);

    const [editingValuation, setEditingValuation] = useState<Valuation | null>(null);
    const [valDialogOpen, setValDialogOpen] = useState(false);

    const [needsRefresh, setNeedsRefresh] = useState(false);

    // *** Setup safe API Calls ***
    const { put, delete: del } = useApiClient();

    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const valuationReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const valuationHeaderReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const exchangeRatesReq = useSafeRequest<any>(api, { maxAttempts: 3 });

    const getValuations = async () => {
        const tasks = [
            valuationReq.run({ method: "GET", url: `valuations/${artworkId}` }),
            valuationHeaderReq.run({ method: "GET", url: `valuations/header/${artworkId}` }),
            exchangeRatesReq.run({ method: "GET", url: `exchangerates/${currency}` }),

            //Other data requirements...
        ];

        const [v, h, e] = await Promise.allSettled(tasks);

        if (v.status === "fulfilled") setVals(v.value);
        if (h.status === "fulfilled") setValuationHeader(h.value);
        if (e.status === "fulfilled") setConversionRates(e.value);
    };


    const renderCurrentBaseValue = (v: Valuation) => {

        if (conversionRates) {
            const fromRate = conversionRates[v.currencyCode] || 1;
            const toRate = conversionRates[currency] || 1;
            const convertedLowValue = (v.lowerRangeAmount / fromRate) * toRate;
            const convertedHighValue = (v.upperRangeAmount / fromRate) * toRate;

            if (v.valuationTypeId === 4) {
                return `≈ ${Math.round(convertedLowValue).toLocaleString()}–${Math.round(convertedHighValue).toLocaleString()} ${currency}`;
            } else {
                return `≈ ${Math.round(convertedLowValue).toLocaleString()} ${currency}`;
            }
        }
    };

    React.useEffect(() => {
        const fetchValuations = async () => {
            try {
                setLoadingValuation(true);
                getValuations().catch(() => { });

                

            } catch (error) {
                console.error('Failed to fetch valuation:', error);
            } finally {
                setLoadingValuation(false);
                setNeedsRefresh(false);
            }
        };

        fetchValuations();

    }, [artworkId, reloadToken, needsRefresh]);

    const stillLoading = valuationReq.loading || exchangeRatesReq.loading; 

    const latest = vals?.[0];

    const handleEdit = (valuation: Valuation) => {
        setEditingValuation(valuation);
        setValDialogOpen(true);
    };

    const handleDelete = async (valuation: Valuation) => {

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        try {

            if (valuation) {
                const res = await del(`valuations/${artworkId}/${valuation.id}`, accessToken);

                setNeedsRefresh(true);
            }

        } catch (error) {
            console.error("Valuation deletion failed:", error);
        }
    };

    const handleUpdate = async (valuationData: FormData) => {

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        try {

            if (valuationData && editingValuation) {

                const res = await put(`valuations/${artworkId}/${editingValuation.id}`, accessToken, valuationData);

                setNeedsRefresh(true); 
            }

        } catch (error) {
            console.error("Valuation update failed:", error);
        }

        setEditingValuation(null);
        setValDialogOpen(false);
    };

    React.useEffect(() => {
    const valuationRecord = vals?.[0];

    if (valuationRecord) {
        const valuationDetails = {
            ValuationCard: {
                date: valuationRecord.valuationDate,
                valuation: valuationRecord.valuationTypeId === 4
                    ? `${valuationRecord.lowerRangeAmount?.toLocaleString()}–${valuationRecord.upperRangeAmount?.toLocaleString()}`
                    : `${valuationRecord.lowerRangeAmount?.toLocaleString()} ${valuationRecord.currencyCode}`,
                currency: valuationRecord.currencyCode,
                baseCurrencyValue: renderCurrentBaseValue(valuationRecord),
                valuationType: valuationRecord.valuationType,
                valuer: valuationRecord.valuerName,
                externalValuer: valuationRecord.externalValuer,
            }
        };

        setLatestValuation(valuationDetails.ValuationCard);
        }
    }, [vals]);

    if (stillLoading) return (<div>Loading...</div>);

    return (
        <>
        <Paper elevation={3} square={false} sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
            {/*<Box display="flex" justifyContent="space-between" alignItems="center">*/}
            {/*    <Typography variant="h6">Values</Typography>*/}
            {/*        <button className="responsive-button" onClick={onAdd} hidden={true}>Add New Value</button>*/}
            {/*</Box>*/}
            {/*<Divider sx={{ my: 2 }} />*/}
            {stillLoading ? <Typography>Loading...</Typography> : (
                    <>
                        <ValuationHeader
                            artworkId={valuationHeader?.artworkId}
                            acquisition={valuationHeader?.acquisition}
                            insurance={valuationHeader?.insurance}
                            fairMarketValue={valuationHeader?.fairMarketValue}
                            exchangeRates={conversionRates ?? null}
                        />
                    <div className="section-header">
                        <h3>Values History</h3>
                    </div>
                    <ValuationGrid
                            valuations={vals}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                    />
                </>
            )}
            </Paper>

            {editingValuation && (
                <AddValuationDialog
                    open={valDialogOpen}
                    onClose={() => { setEditingValuation(null); setEditingValuation(null); }}
                    artworkId={artworkId}
                    onSave={handleUpdate}
                    valuation={editingValuation}
                />
            )}
        </>
    );
};

export default ValuationsPanel;
